import { useEffect, useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import {
  Route,
  Navigate,
  Routes as RoutesDom,
  useLocation,
  useSearchParams,
  useNavigate,
} from "react-router-dom";
import { RouteConstants } from "../constants";
import { logout } from "../store/slices/authSlice";
import { LoginLocal, Login } from "../components/features/login";
import { Signup, VerifyEmail } from "../components/features/signup";
import ForgotPassword from "../components/features/forgotPassword";
import ResetPassword from "../components/features/resetPassword";
import Home from "../components/features/home";
import SetPassword from "../components/features/setPassword";
import OtpLogin from "../components/features/otpLogin";
import SsoComplete from "../components/features/sso/SsoComplete";
import SsoError from "../components/features/sso/SsoError";
import MfaSignIn from "../components/features/mfa";
import EmergencySignIn from "../components/features/emergencySignIn";
import { useMountEffect, usePrevious } from "primereact/hooks";
import * as GlobalHelper from "../helpers/GlobalHelper";
import * as PendingRedirect from "../helpers/PendingRedirect";

const Routes = ({ auth, global, logout }) => {
  const [searchParams] = useSearchParams();
  const [redirectPath, setRedirectPath] = useState(null);
  const previousRefreshToken = usePrevious(auth?.session?.refreshToken);
  const navigate = useNavigate();
  const location = useLocation();
  const isRootDomain = GlobalHelper.CheckIfRootDomain(
    window.location.hostname.split(".")[0]
  );

  useMountEffect(() => {
    if (searchParams.get("params") && auth?.session?.refreshToken) {
      logout();
      setRedirectPath(location);
    }
  });

  // Signed out: keep a report or certificate download link so Home opens it
  // after sign-in.
  useEffect(() => {
    if (!auth?.session?.refreshToken) {
      PendingRedirect.rememberDownloadPath(location.pathname);
    }
  }, [auth?.session?.refreshToken, location.pathname]);

  useEffect(() => {
    if (previousRefreshToken && !auth?.session?.refreshToken) {
      if (redirectPath) {
        navigate(redirectPath, { replace: true });
        setRedirectPath(null);
      } else {
        navigate(RouteConstants.APP_ROUTE_LOGIN, { replace: true });
      }
    }
  }, [
    auth?.session?.refreshToken,
    previousRefreshToken,
    navigate,
    redirectPath,
  ]);

  if (!auth?.session?.refreshToken) {
    return (
      <RoutesDom>
        <Route
          exact
          path={RouteConstants.APP_ROUTE_FORGOT_PASSWORD}
          element={<ForgotPassword />}
        />
        <Route
          exact
          path={RouteConstants.APP_ROUTE_RESET_PASSWORD}
          element={<ResetPassword />}
        />
        <Route
          exact
          path={RouteConstants.APP_ROUTE_SET_PASSWORD}
          element={<SetPassword />}
        />
        <Route
          exact
          path={RouteConstants.APP_ROUTE_LOGIN_LOCAL}
          element={<LoginLocal />}
        />
        {(!isRootDomain && (
          <Route
            exact
            path={RouteConstants.APP_ROUTE_LOGIN}
            element={<Login />}
          />
        )) ||
          null}
        {(!isRootDomain && global?.enterprise?.isSelfRegistrationEnabled && (
          <Route
            exact
            path={RouteConstants.APP_ROUTE_SIGN_UP}
            element={<Signup />}
          />
        )) ||
          null}
        {(!isRootDomain && (
          <Route
            path={RouteConstants.APP_ROUTE_VERIFY_EMAIL}
            element={<VerifyEmail />}
          />
        )) ||
          null}
        <Route
          exact
          path={RouteConstants.APP_ROUTE_OTP}
          element={<OtpLogin />}
        />
        <Route
          exact
          path={RouteConstants.APP_ROUTE_LOGIN_MFA}
          element={<MfaSignIn />}
        />
        {(!isRootDomain && (
          <Route
            exact
            path={RouteConstants.APP_ROUTE_EMERGENCY_SIGN_IN}
            element={<EmergencySignIn />}
          />
        )) ||
          null}
        {(!isRootDomain && (
          <Route
            exact
            path={RouteConstants.APP_ROUTE_SSO_COMPLETE}
            element={<SsoComplete />}
          />
        )) ||
          null}
        {(!isRootDomain && (
          <Route
            exact
            path={RouteConstants.APP_ROUTE_SSO_ERROR}
            element={<SsoError />}
          />
        )) ||
          null}
        <Route
          index
          path="/*"
          element={
            <Navigate
              to={
                isRootDomain
                  ? RouteConstants.APP_ROUTE_LOGIN_LOCAL
                  : RouteConstants.APP_ROUTE_LOGIN
              }
              replace
            />
          }
        />
      </RoutesDom>
    );
  } else {
    return (
      <RoutesDom>
        {/* A company portal sign-in can arrive while someone is signed in: it replaces that session. */}
        <Route
          path={RouteConstants.APP_ROUTE_SSO_COMPLETE}
          element={<SsoComplete />}
        />
        <Route path="/*" element={<Home />} />
      </RoutesDom>
    );
  }
};

Routes.propTypes = {
  auth: PropTypes.object.isRequired,
  global: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  auth: state.auth,
  global: state.global,
});

const mapDispatchToProps = (dispatch) => ({
  logout: () => dispatch(logout()),
});

export default connect(mapStateToProps, mapDispatchToProps)(Routes);
